'use client';
import { useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import Head from 'next/head';
import * as Sections from '@/components/templates/sections';
import { replaceVariables } from '@/lib/template-engine';
import { DesignSettings, DesignProvider } from '@/context/DesignContext';

const DEFAULT_DESIGN: DesignSettings = {
  colors: {
    primary: '#1A4D8C',
    secondary: '#FF6B6B',
    background: '#FFFFFF',
    text: '#334155',
    textLight: '#64748B',
    border: '#E2E8F0',
  },
  typography: {
    headingFont: "'Sora', sans-serif",
    bodyFont: "'Plus Jakarta Sans', sans-serif",
    baseFontSize: '16px',
    headingWeights: { h1: 700, h2: 600, h3: 600, h4: 500 },
  },
  spacing: {
    containerMaxWidth: '1200px',
    sectionPaddingTop: '80px',
    sectionPaddingBottom: '80px',
    gutter: '24px',
  },
  effects: {
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    borderRadius: '8px',
  },
  buttons: {
    primary: { padding: '12px 28px', textColor: '#FFFFFF', borderRadius: '9999px', backgroundColor: '#1A4D8C' },
    secondary: { padding: '12px 28px', textColor: '#1A4D8C', borderRadius: '9999px', backgroundColor: 'transparent', borderColor: '#1A4D8C' },
    outline: { padding: '12px 28px', textColor: '#1A4D8C', borderRadius: '9999px', backgroundColor: 'transparent', borderColor: '#1A4D8C' },
  },
};

export default function StorefrontPage() {
  const { subdomain } = useParams();
  const [storeData, setStoreData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!subdomain) return;
    const fetchStore = async () => {
      try {
        console.log('[Storefront] Fetching /api/public/store/', subdomain);
        const res = await fetch(`/api/public/store/${subdomain}`);
        if (!res.ok) {
          const text = await res.text();
          console.error('[Storefront] Response not ok, text:', text);
          throw new Error(`HTTP ${res.status}: ${text}`);
        }
        const result = await res.json();
        console.log('[Storefront] Full API response:', result);
        console.log('[Storefront] result.success:', result.success);
        console.log('[Storefront] result.data:', result.data);
        if (result.data) {
          console.log('[Storefront] result.data.theme:', result.data.theme);
        }
        // The API returns { success: true, data: { ... } }
        if (result.success && result.data) {
          setStoreData(result.data);
          console.log('[Storefront] Setting storeData:', result.data);
        } else {
          throw new Error(result.error || 'Store not found');
        }
      } catch (err: any) {
        console.error('[Storefront] Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStore();
  }, [subdomain]);

  // Separate effect to log when storeData is set
  useEffect(() => {
    if (storeData) {
      console.log('[Storefront] storeData is now:', storeData);
      console.log('[Storefront] storeData.theme:', storeData.theme);
      if (storeData.theme) {
        console.log('[Storefront] storeData.theme.json_config:', storeData.theme.json_config);
        console.log('[Storefront] storeData.theme.data_config:', storeData.theme.data_config);
        console.log('[Storefront] storeData.theme.color_config:', storeData.theme.color_config);
      }
    }
  }, [storeData]);

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Chargement du magasin…</div>;
  }

  if (error || !storeData) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>{error || 'Magasin introuvable'}</div>;
  }

  if (!storeData.theme) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Ce magasin n'a pas de template actif.</p>
        <p>Veuillez sélectionner un template dans le tableau de bord.</p>
      </div>
    );
  }

  const { theme, name } = storeData;
  const designSettings = theme.color_config?.designSettings || theme.color_config || DEFAULT_DESIGN;
  let jsonConfig = theme.json_config || {};
  
  // Si jsonConfig est une chaîne JSON (possible si mal stocké), parse-la !
  if (typeof jsonConfig === 'string') {
    try {
      jsonConfig = JSON.parse(jsonConfig);
    } catch (e) {
      console.error('Erreur lors du parse de json_config:', e);
      jsonConfig = {};
    }
  }
  
  let dataConfig = theme.data_config || {};
  if (typeof dataConfig === 'string') {
    try {
      dataConfig = JSON.parse(dataConfig);
    } catch (e) {
      console.error('Erreur lors du parse de data_config:', e);
      dataConfig = {};
    }
  }

  console.log('[Storefront] jsonConfig:', jsonConfig);
  console.log('[Storefront] dataConfig:', dataConfig);

  const processedConfig = replaceVariables(jsonConfig, dataConfig);
  console.log('[Storefront] processedConfig:', processedConfig);
  
  // Déterminer la home page
  let homePage: any = null;
  
  if (processedConfig.pages) {
    homePage = processedConfig.pages.home || processedConfig.pages[Object.keys(processedConfig.pages)[0]];
  } else if (processedConfig.sections) {
    // Fallback : si pas de pages, utiliser directement les sections à la racine
    homePage = { sections: processedConfig.sections };
  }

  console.log('[Storefront] homePage:', homePage);
  
  if (!homePage || !homePage.sections) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Aucune page à afficher</h2>
        <p>Le template de ce magasin ne contient pas de contenu valide.</p>
      </div>
    );
  }

  const styles = jsonConfig.styles || [];
  const sections = homePage.sections.filter((s: any) => s.visible !== false);

  return (
    <>
      <Head>
        <title>{name || 'Boutique'}</title>
        {styles.map((href: string) => (
          <link key={href} rel="stylesheet" href={href} />
        ))}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>
      <DesignProvider design={designSettings}>
        {sections.map((section: any, idx: number) => {
          const Component = Sections[section.type as keyof typeof Sections];
          if (!Component) {
            console.warn(`Composant "${section.type}" non trouvé`);
            return null;
          }
          return (
            <div
              key={`${idx}-${section.type}`}
              data-section-type={section.type}
              className="section-wrapper"
            >
              <Component {...section.props} design={designSettings} />
            </div>
          );
        })}
      </DesignProvider>
    </>
  );
}